'use client';

import 'instantsearch.css/themes/reset.css';

import { InstantMeiliSearchInstance } from '@meilisearch/instant-meilisearch';
import { Configure, InstantSearch, SearchBox, useInstantSearch } from 'react-instantsearch';
import CustomPagination from '@/components/search/custom/CustomPagination';
import initializeClient from '@/lib/meilisearch/client';
import { useEffect, useState, useRef } from 'react';
import { INDEX_FORMATIONS } from './types';

import TitleH2 from '../common/TitleH2';
import CustomMenu from './custom/CustomMenu';
import CustomSortBy from './custom/CustomSortBy';
import AttributeToggle from './custom/AttributeToggle';
import DateRangeFilter from './custom/DateRangeFilter';
import { AppConfig } from '@/utils/AppConfig';
import CustomHitsFormations from './hits/CustomHitFormation';
import CustomClearRefinements from './custom/CustomClear';

interface Props {
  title?: string;
  displaySearchBar?: boolean;
}

export default function SearchFormations({ title, displaySearchBar = false }: Props) {
  let [searchClient, setSearchClient] = useState<InstantMeiliSearchInstance | null>(null);
  let [currentPage, setCurrentPage] = useState<number | null>(null);
  const searchElement = useRef<null | HTMLDivElement>(null);

  const executeScroll = () =>
    searchElement?.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });

  useEffect(() => {
    const initClient = async () => {
      const searchClient = await initializeClient();
      setSearchClient(searchClient);
    };

    initClient();
  }, []);

  function PaginationScroll() {
    const { indexUiState } = useInstantSearch();
    useEffect(() => {
      setCurrentPage(indexUiState.page || 0);
    }, [indexUiState]);

    return <></>;
  }

  useEffect(() => {
    if (currentPage !== null) {
      executeScroll();
    }
  }, [currentPage]);

  let initialUiState: any = {
    [`${INDEX_FORMATIONS}:date_published:desc`]: {
      refinementList: {
        type: ['formation'],
      },
    },
  };
  const [typeValue, setTypeValue] = useState('formation');

  return (
    <div className="flex min-h-[400px] flex-col">
      <div className="gap-5">
        {title && (
          <TitleH2
            size="medium"
            customFont={true}
            color="black"
            titleTracking="tracking-wide"
            heading={true}
          >
            {title}
          </TitleH2>
        )}
      </div>
      {!searchClient && (
        <div className="flex w-full flex-col gap-4">
          <div className="flex items-center gap-4">
            <div className="skeleton h-16 w-16 shrink-0 rounded-full"></div>
            <div className="flex flex-col gap-4">
              <div className="skeleton h-4 w-20"></div>
              <div className="skeleton h-4 w-28"></div>
            </div>
          </div>
          <div className="skeleton h-full w-full"></div>
        </div>
      )}
      {searchClient && (
        <InstantSearch
          indexName={`${INDEX_FORMATIONS}:date_published:desc`}
          searchClient={searchClient}
          initialUiState={initialUiState}
        >
          <div className="relative mx-auto flex w-full animate-appear flex-col items-center justify-center opacity-0 transition-opacity duration-1000 ease-in-out">
            {displaySearchBar && (
              <SearchBox
                classNames={{
                  root: 'w-full p-1',
                  form: 'relative w-full',
                  input:
                    'block h-12 w-full pl-14 pr-5 py-2 bg-white border border-slate-300 placeholder-slate-400 focus:outline-none focus:border-sky-500 focus:ring-sky-500 rounded-full focus:ring-1',
                  submitIcon: 'absolute top-4 left-6 bottom-0 w-5 h-5',
                  resetIcon: 'absolute top-4 right-6 bottom-0 w-5 h-5',
                }}
              ></SearchBox>
            )}
            <div className="flex w-full flex-wrap gap-2.5 pb-8 pt-4">
              <AttributeToggle
                attribute="type"
                labelMap={{ formation: 'Formations', session: 'Sessions' }}
                defaultValue="formation"
                value={typeValue}
                onChange={setTypeValue}
              />

              <CustomMenu
                key="domain"
                attribute="domain"
                limit={AppConfig.meilisearchCustomMenuLimit}
              />
              <CustomMenu
                key="mode"
                attribute="mode"
                limit={AppConfig.meilisearchCustomMenuLimit}
              />
              <CustomMenu
                key="postal_codes"
                attribute="postal_codes"
                limit={AppConfig.meilisearchCustomMenuLimit}
              />
              <CustomMenu
                key="place"
                attribute="places"
                limit={AppConfig.meilisearchCustomMenuLimit}
              />
              {typeValue === 'session' && (
                <>
                  <DateRangeFilter />
                  <CustomMenu
                    key="sessions_postal_code"
                    attribute="postal_code"
                    limit={AppConfig.meilisearchCustomMenuLimit}
                  />
                  <CustomMenu
                    key="sessions_place"
                    attribute="place"
                    limit={AppConfig.meilisearchCustomMenuLimit}
                  />
                </>
              )}
              <CustomClearRefinements excludedAttributes={['type']} />
            </div>
            <CustomHitsFormations />
          </div>
          <CustomPagination
            padding={2}
            classNames={{
              root: 'flex w-full my-8 justify-center',
              list: 'MyCustomPaginationList MyCustomPaginationList--subclass',
              item: 'flex mx-5 justify-center items-center gap-4 h-10 font-bold',
              selectedItem:
                'flex mx-2 justify-center items-center w-14 h-14 rounded-full gradient-circle font-bold text-white',
            }}
          />
          <PaginationScroll />
        </InstantSearch>
      )}
    </div>
  );
}
